import { useActionMutation } from '../../feedback/useActionMutation';
import { notify, safeActionError, UnconfirmedActionError } from '../../feedback/notifications';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { BrandLogo } from '../../components/BrandLogo';
import { useAuth } from './AuthProvider';
import { safeReturnPath, SESSION_EXPIRED_MESSAGE } from './sessionPolicy';

export function LoginPage() {
  const { user, profile, profileError, loading, sessionState } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [reauthenticated,setReauthenticated]=useState(false);
  const forceReauth=Boolean((location.state as {reauthenticate?:boolean}|null)?.reauthenticate)&&!reauthenticated;

  const action = useActionMutation('auth.login', { mutationFn: async () => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (!data.session) throw new UnconfirmedActionError();
  },onSuccess:()=>setReauthenticated(true) });
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setBusy(true); setError('');
    try { await action.mutateAsync(); } catch (error) { setError(safeActionError(error)); } finally { setBusy(false); }
  };

  if (user && loading) return <main className="center-state" role="status">Loading your accountâ€¦</main>;
  if (user && profileError && sessionState !== 'unavailable' && !forceReauth) return <Navigate to="/dashboard" replace />;
  if (user && profile && sessionState !== 'unavailable' && !forceReauth) return <Navigate to={safeReturnPath((location.state as { from?: unknown } | null)?.from,profile.role)} replace />;
  return <AuthCard title="Welcome back" subtitle="Sign in to manage your deliveries.">
    {(sessionState === 'expired' || (location.state as {reason?:string}|null)?.reason === 'expired') && <p className="alert warning" role="alert">{SESSION_EXPIRED_MESSAGE}</p>}
    {(location.state as {reason?:string}|null)?.reason === 'signedout' && <p className="alert" role="status">You have been signed out. Please sign in again to continue.</p>}
    <form onSubmit={submit} className="stack">
      <label>Email<input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
      <label>Password<input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></label>
      {error && <p className="alert error" role="alert">{error}</p>}
      <button className="button primary" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
      <div className="split-links"><Link to="/forgot-password">Forgot password?</Link><Link to="/register">Create account</Link></div>
    </form>
  </AuthCard>;
}

export function RegisterPage() {
  const [values, setValues] = useState({ name: '', email: '', password: '' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const action = useActionMutation('auth.register', { mutationFn: async () => {
    const { error } = await supabase.auth.signUp({ email: values.email, password: values.password,
      options: { emailRedirectTo: window.location.origin + '/auth/callback', data: { full_name: values.name } } });
    if (error) throw error;
  } });
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setBusy(true); setError(''); setMessage('');
    try { await action.mutateAsync(); setMessage('Check your email and confirm your address before placing an order.'); }
    catch (error) { setError(safeActionError(error)); } finally { setBusy(false); }
  };
  return <AuthCard title="Create your account" subtitle="Email confirmation is required for ordering.">
    <form onSubmit={submit} className="stack">
      <label>Full name<input autoComplete="name" value={values.name} onChange={(e) => setValues({ ...values, name: e.target.value })} minLength={2} required /></label>
      <label>Email<input type="email" autoComplete="email" value={values.email} onChange={(e) => setValues({ ...values, email: e.target.value })} required /></label>
      <label>Password<input type="password" autoComplete="new-password" minLength={8} value={values.password} onChange={(e) => setValues({ ...values, password: e.target.value })} required /><small>Use at least 8 characters.</small></label>
      {error && <p className="alert error" role="alert">{error}</p>}
      {message && <p className="alert success" role="status">{message}</p>}
      <button className="button primary" disabled={busy}>{busy ? 'Creating…' : 'Create account'}</button>
      <p className="muted">Already registered? <Link to="/login">Sign in</Link></p>
    </form>
  </AuthCard>;
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const action = useActionMutation('auth.reset', { mutationFn: async () => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin + '/auth/callback?recovery=1' });
    if (error) throw error;
  } });
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setMessage('');
    try { await action.mutateAsync(); setMessage('If the account exists, a reset link has been sent.'); }
    catch (error) { setMessage(safeActionError(error)); }
  };
  return <AuthCard title="Reset password" subtitle="We’ll email you a secure reset link.">
    <form onSubmit={submit} className="stack"><label>Email<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></label><button className="button primary" disabled={action.isPending}>{action.isPending ? 'Requesting…' : 'Send reset link'}</button>{message && <p className="alert">{message}</p>}</form>
  </AuthCard>;
}

export function AuthCallbackPage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const hash = new URLSearchParams(location.hash.replace(/^#/, ''));
  const recovery = params.get('recovery') === '1' || params.get('type') === 'recovery' || hash.get('type') === 'recovery';
  const callbackError = params.get('error_description') ?? hash.get('error_description') ?? params.get('error') ?? hash.get('error');
  const [state, setState] = useState<'loading' | 'ready' | 'invalid'>(callbackError ? 'invalid' : 'loading');
  const [message, setMessage] = useState(callbackError ? 'This confirmation or recovery link is unavailable. Request a new email.' : '');
  const [passwords, setPasswords] = useState({ password: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    if (callbackError) return;
    let active = true;
    let established = false;
    const acceptSession = (session: unknown) => {
      if (!active || !session) return;
      established = true;
      setState('ready');
    };
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') acceptSession(session);
    });
    const deadline = window.setTimeout(() => {
      if (active && !established) {
        setMessage('This link could not be verified. Check your connection or request a new email.');
        setState('invalid');
      }
    }, 5_000);
    void (async () => {
      const { data, error } = await supabase.auth.getSession();
      // A newer recovery event wins over a late initial session read.
      if (!active || established) return;
      if (error) { if (active) { setMessage(safeActionError(error)); setState('invalid'); } return; }
      if (data.session) { acceptSession(data.session); return; }
      const code = params.get('code');
      if (code) {
        const exchanged = await supabase.auth.exchangeCodeForSession(code);
        if (exchanged.data.session) { acceptSession(exchanged.data.session); return; }
        if (active && exchanged.error) setMessage(safeActionError(exchanged.error));
      }
    })().catch(error => { if (active && !established) { setMessage(safeActionError(error)); setState('invalid'); } });
    return () => { active = false; window.clearTimeout(deadline); listener.subscription.unsubscribe(); };
  }, [callbackError, location.search]);

  const passwordAction = useActionMutation('auth.password', { mutationFn: async () => {
    const { error } = await supabase.auth.updateUser({ password: passwords.password });
    if (error) throw error;
    const { error: closeError } = await supabase.auth.signOut();
    if (closeError) {
      notify.warning('Password changed; session closure unconfirmed', { description: 'Sign out safely before leaving this device. Do not submit another password change to close the session.' });
      return false;
    }
    return true;
  } });
  const updatePassword = async (event: FormEvent) => {
    event.preventDefault();
    const validation = validateNewPassword(passwords.password, passwords.confirm);
    if (validation) { setMessage(validation); notify.warning('Review your passwords', { id: 'password-validation', description: validation }); return; }
    setBusy(true); setMessage('');
    try {
      const closed = await passwordAction.mutateAsync();
      if (closed) setComplete(true);
      else setMessage('Password changed, but the recovery session could not be closed. Sign out before leaving this device.');
    } catch (error) { setMessage(safeActionError(error)); } finally { setBusy(false); }
  };

  if (state === 'loading') return <AuthCard title={recovery ? 'Verify reset link' : 'Confirming email'} subtitle="Supabase is securely establishing this session."><p className="alert" role="status">Checking the link...</p></AuthCard>;
  if (state === 'invalid') return <AuthCard title="Link unavailable" subtitle="The confirmation or recovery session could not be verified."><p className="alert error" role="alert">{message}</p><Link className="button primary" to={recovery ? '/forgot-password' : '/login'}>{recovery ? 'Request a new reset link' : 'Return to sign in'}</Link></AuthCard>;
  if (recovery) return <AuthCard title={complete ? 'Password changed' : 'Choose a new password'} subtitle={complete ? 'Your recovery session has been closed.' : 'Use a unique password with at least 8 characters.'}>{complete ? <><p className="alert success" role="status">Your password was updated. Sign in again with the new password.</p><Link className="button primary" to="/login">Sign in</Link></> : <form className="stack" onSubmit={updatePassword}><label>New password<input type="password" autoComplete="new-password" minLength={8} value={passwords.password} onChange={(event) => setPasswords({ ...passwords, password: event.target.value })} required /></label><label>Confirm new password<input type="password" autoComplete="new-password" minLength={8} value={passwords.confirm} onChange={(event) => setPasswords({ ...passwords, confirm: event.target.value })} required /></label>{message && <p className="alert error" role="alert">{message}</p>}<button className="button primary" disabled={busy}>{busy ? 'Updating...' : 'Update password'}</button></form>}</AuthCard>;
  return <AuthCard title="Email confirmed" subtitle="Supabase verified your confirmation session."><p className="alert success">Your email is confirmed and your session is ready.</p><Link className="button primary" to="/dashboard">Continue</Link></AuthCard>;
}

export function validateNewPassword(password: string, confirmation: string) {
  if (password.length < 8) return 'Use at least 8 characters.';
  if (password !== confirmation) return 'The passwords do not match.';
  return '';
}

function AuthCard({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return <main className="auth-shell"><BrandLogo to="/" /><section className="auth-card"><h1>{title}</h1><p className="muted">{subtitle}</p>{children}</section></main>;
}
